import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  DocumentData,
  DocumentSnapshot,
  QuerySnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './config';
import { InventoryItem, CheckInEvent, HistoryEvent } from '../types/models';

export class FirestoreService {
  // Collections
  private static INVENTORY_COLLECTION = 'inventory';
  private static USERS_COLLECTION = 'users';
  private static EVENTS_COLLECTION = 'events';

  // Inventory Item Operations
  static async getAllInventoryItems(): Promise<InventoryItem[]> {
    try {
      const querySnapshot = await getDocs(collection(db, this.INVENTORY_COLLECTION));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as InventoryItem));
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  }

  static async getInventoryItem(itemId: string): Promise<InventoryItem | null> {
    try {
      const docRef = doc(db, this.INVENTORY_COLLECTION, itemId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as InventoryItem;
      }
      return null;
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      throw error;
    }
  }

  static async addInventoryItem(item: Omit<InventoryItem, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.INVENTORY_COLLECTION), {
        ...item,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding inventory item:', error);
      throw error;
    }
  }

  static async updateInventoryItem(itemId: string, updates: Partial<InventoryItem>): Promise<void> {
    try {
      const docRef = doc(db, this.INVENTORY_COLLECTION, itemId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  }

  static async deleteInventoryItem(itemId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.INVENTORY_COLLECTION, itemId));
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw error;
    }
  }

  // Check In/Out Operations
  static async checkOutItem(
    itemId: string,
    userName: string,
    notes?: string,
    itemName?: string,
    organizationID?: string
  ): Promise<void> {
    try {
      const checkInEvent: Omit<CheckInEvent, 'eventID'> = {
        eventDate: Date.now() / 1000, // Convert to seconds like iOS app
        userName,
        checkedOut: true,
      };

      // Add the check-in event (with denormalized fields for history queries)
      await addDoc(collection(db, this.EVENTS_COLLECTION), {
        ...checkInEvent,
        itemId: itemId,
        ...(notes ? { notes } : {}),
        ...(itemName ? { itemName } : {}),
        ...(organizationID ? { organizationID } : {}),
        createdAt: serverTimestamp()
      });

      // Update inventory item status
      await this.updateInventoryItem(itemId, {
        checkedOut: true,
        lastUpdated: Date.now() / 1000,
        checkedOutTo: userName,
        checkedOutDate: Date.now() / 1000
      });
    } catch (error) {
      console.error('Error checking out item:', error);
      throw error;
    }
  }

  static async checkInItem(
    itemId: string,
    userName: string,
    notes?: string,
    itemName?: string,
    organizationID?: string
  ): Promise<void> {
    try {
      const checkInEvent: Omit<CheckInEvent, 'eventID'> = {
        eventDate: Date.now() / 1000,
        userName,
        checkedOut: false,
      };

      // Add the check-in event (with denormalized fields for history queries)
      await addDoc(collection(db, this.EVENTS_COLLECTION), {
        ...checkInEvent,
        itemId: itemId,
        ...(notes ? { notes } : {}),
        ...(itemName ? { itemName } : {}),
        ...(organizationID ? { organizationID } : {}),
        createdAt: serverTimestamp()
      });

      // Update inventory item status
      await this.updateInventoryItem(itemId, {
        checkedOut: false,
        lastUpdated: Date.now() / 1000,
        checkedOutTo: deleteField(),
        checkedOutDate: deleteField()
      });
    } catch (error) {
      console.error('Error checking in item:', error);
      throw error;
    }
  }

  // Real-time subscriptions
  static subscribeToInventoryItems(
    callback: (items: InventoryItem[]) => void,
    organizationID?: string
  ): Unsubscribe {
    const constraints: any[] = [];
    if (organizationID) {
      constraints.push(where('organizationID', '==', organizationID));
    }
    constraints.push(orderBy('itemName'));
    const q = query(collection(db, this.INVENTORY_COLLECTION), ...constraints);

    return onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const items = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Convert Firestore Timestamps to numbers for Redux serialization
        if (data.createdAt?.toMillis) {
          data.createdAt = data.createdAt.toMillis();
        }
        if (data.updatedAt?.toMillis) {
          data.updatedAt = data.updatedAt.toMillis();
        }
        return {
          id: doc.id,
          ...data
        } as InventoryItem;
      });
      callback(items);
    }, (error) => {
      console.error('[FirestoreService] Inventory subscription error:', error);
    });
  }

  static subscribeToItemEvents(itemId: string, callback: (events: CheckInEvent[]) => void): Unsubscribe {
    const q = query(
      collection(db, this.EVENTS_COLLECTION),
      where('itemId', '==', itemId),
      orderBy('eventDate', 'desc')
    );

    return onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const events = querySnapshot.docs.map(doc => ({
        eventID: doc.id,
        ...doc.data()
      } as CheckInEvent));
      callback(events);
    });
  }

  // Search and filter
  static async searchInventoryItems(searchTerm: string): Promise<InventoryItem[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a simple contains search - for production, consider using Algolia or similar
      const allItems = await this.getAllInventoryItems();
      return allItems.filter(item => 
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.itemGroup && item.itemGroup.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } catch (error) {
      console.error('Error searching inventory items:', error);
      throw error;
    }
  }

  static async getInventoryItemsByGroup(group: string): Promise<InventoryItem[]> {
    try {
      const q = query(
        collection(db, this.INVENTORY_COLLECTION),
        where('itemGroup', '==', group)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as InventoryItem));
    } catch (error) {
      console.error('Error fetching items by group:', error);
      throw error;
    }
  }

  static async getCheckedOutItems(): Promise<InventoryItem[]> {
    try {
      const q = query(
        collection(db, this.INVENTORY_COLLECTION),
        where('checkedOut', '==', true)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as InventoryItem));
    } catch (error) {
      console.error('Error fetching checked out items:', error);
      throw error;
    }
  }

  // History / Events queries

  // Subscribe to all events in real-time, ordered by date descending
  static subscribeToAllEvents(
    callback: (events: HistoryEvent[]) => void,
    organizationID?: string
  ): Unsubscribe {
    const constraints: any[] = [];
    if (organizationID) {
      constraints.push(where('organizationID', '==', organizationID));
    }
    constraints.push(orderBy('eventDate', 'desc'));
    const q = query(collection(db, this.EVENTS_COLLECTION), ...constraints);

    return onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const events = querySnapshot.docs.map(doc => {
        const data = doc.data();
        if (data.createdAt?.toMillis) {
          data.createdAt = data.createdAt.toMillis();
        }
        return {
          eventID: doc.id,
          ...data
        } as HistoryEvent;
      });
      callback(events);
    }, (error) => {
      console.error('[FirestoreService] Events subscription error:', error);
    });
  }

  // Get paginated events with optional cursor
  static async getEventsPaginated(
    pageSize: number = 50,
    lastDoc?: DocumentSnapshot
  ): Promise<{ events: HistoryEvent[]; lastDoc: DocumentSnapshot | null }> {
    try {
      let q = query(
        collection(db, this.EVENTS_COLLECTION),
        orderBy('eventDate', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(
          collection(db, this.EVENTS_COLLECTION),
          orderBy('eventDate', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map(doc => {
        const data = doc.data();
        if (data.createdAt?.toMillis) {
          data.createdAt = data.createdAt.toMillis();
        }
        return {
          eventID: doc.id,
          ...data
        } as HistoryEvent;
      });

      const newLastDoc = querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

      return { events, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching paginated events:', error);
      throw error;
    }
  }

  // Bulk operations (Firestore batches support up to 500 writes)

  static async bulkDeleteItems(itemIds: string[]): Promise<void> {
    const batch = writeBatch(db);
    for (const id of itemIds) {
      batch.delete(doc(db, this.INVENTORY_COLLECTION, id));
    }
    await batch.commit();
  }

  static async bulkCheckIn(
    itemIds: string[],
    userName: string,
    organizationID?: string
  ): Promise<void> {
    const batch = writeBatch(db);
    const now = Date.now() / 1000;

    for (const id of itemIds) {
      const itemRef = doc(db, this.INVENTORY_COLLECTION, id);
      batch.update(itemRef, {
        checkedOut: false,
        lastUpdated: now,
        checkedOutTo: deleteField(),
        checkedOutDate: deleteField(),
        updatedAt: serverTimestamp(),
      });

      const eventRef = doc(collection(db, this.EVENTS_COLLECTION));
      batch.set(eventRef, {
        eventDate: now,
        userName,
        checkedOut: false,
        itemId: id,
        ...(organizationID ? { organizationID } : {}),
        createdAt: serverTimestamp(),
      });
    }

    await batch.commit();
  }

  static async bulkCheckOut(
    itemIds: string[],
    userName: string,
    organizationID?: string
  ): Promise<void> {
    const batch = writeBatch(db);
    const now = Date.now() / 1000;

    for (const id of itemIds) {
      const itemRef = doc(db, this.INVENTORY_COLLECTION, id);
      batch.update(itemRef, {
        checkedOut: true,
        lastUpdated: now,
        checkedOutTo: userName,
        checkedOutDate: now,
        updatedAt: serverTimestamp(),
      });

      const eventRef = doc(collection(db, this.EVENTS_COLLECTION));
      batch.set(eventRef, {
        eventDate: now,
        userName,
        checkedOut: true,
        itemId: id,
        ...(organizationID ? { organizationID } : {}),
        createdAt: serverTimestamp(),
      });
    }

    await batch.commit();
  }

  static async bulkUpdateGroup(itemIds: string[], group: string): Promise<void> {
    const batch = writeBatch(db);
    for (const id of itemIds) {
      const itemRef = doc(db, this.INVENTORY_COLLECTION, id);
      batch.update(itemRef, {
        itemGroup: group,
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }
}